import {useState, useCallback, useEffect} from 'react'
import type {RenderCommand, RenderMessage, PreviewData} from './types'
import {RenderState} from './types'

function onMessage(
  event: MessageEvent,
  expectedOrigin: string,
  targetOrigin: string,
  iFrameRef: React.RefObject<HTMLIFrameElement | null>,
  containerRef: React.RefObject<HTMLDivElement | null>,
  setErrorMessage: (error: string) => void,
  setRenderState: (state: RenderState) => void,
) {
  const renderWindow = iFrameRef.current?.contentWindow
  if (event.origin !== expectedOrigin || event.source !== renderWindow) return

  const message = getMessage(event)
  if (!message) return

  const body = message.body

  switch (body) {
    case 'hello':
      {
        const ackmsg = {
          type: 'render:cmd',
          body: {
            cmd: 'ack',
            ack: true,
          },
        } as const

        const msg = {
          type: 'render:cmd',
          body: {
            cmd: 'branding',
            branding: false,
          },
        } as const

        sendRenderCommand(renderWindow, ackmsg, targetOrigin)
        sendRenderCommand(renderWindow, msg, targetOrigin)
      }
      break
    case 'error':
    case 'error:fatal':
    case 'error:invalid':
      setRenderState(RenderState.ERROR)
      if (message.payload?.error) {
        setErrorMessage(message.payload.error)
      }
      break
    case 'loading':
      setRenderState(RenderState.LOADING)
      break
    case 'loaded':
      if (!iFrameRef.current || !containerRef.current) return

      setRenderState(RenderState.LOADED)
      break
    case 'ready':
      if (!iFrameRef.current || !containerRef.current) return

      setRenderState(RenderState.READY)
      break
    default:
      break
  }
}

function sendDataToIFrame(
  iframe: HTMLIFrameElement,
  container: HTMLDivElement,
  renderableData: PreviewData,
  viewscreenOrigin: string,
) {
  const msg = {
    type: 'render:cmd',
    body: {
      cmd: 'sandbox:data',
      'sandbox:data': {
        ...renderableData,
      },
    },
  } as const

  sendRenderCommand(iframe.contentWindow, msg, viewscreenOrigin)
}

function sendRenderCommand(renderWindow: Window | null | undefined, message: RenderCommand, targetOrigin: string) {
  if (renderWindow && renderWindow.postMessage) {
    renderWindow.postMessage(JSON.stringify(message), targetOrigin)
  }
}

function getMessage(event: MessageEvent): RenderMessage | null {
  let data = event.data as unknown

  if (!data) return null

  if (typeof data === 'string') {
    try {
      data = JSON.parse(data) as unknown
    } catch {
      // Ignore parse errors
      return null
    }
  }

  if (!isRenderMessage(data)) return null

  return data
}

function isRenderMessage(data: unknown): data is RenderMessage {
  if (typeof data !== 'object' || !data) {
    return false
  }

  const messageData = data as RenderMessage
  return (
    messageData.type === 'render' && typeof messageData.body === 'string' && typeof messageData.payload === 'object'
  )
}

export function useIFrameMessaging(
  iFrameRef: React.RefObject<HTMLIFrameElement | null>,
  containerRef: React.RefObject<HTMLDivElement | null>,
  previewData: PreviewData,
  viewscreenOrigin: string,
  opaqueOrigin = false,
) {
  const [renderState, setRenderState] = useState<RenderState>(RenderState.LOADING)
  const [errorMessage, setErrorMessage] = useState<string>()
  // Frames without allow-same-origin report an opaque origin and can only receive messages sent to "*".
  // The source-window check in onMessage still binds incoming messages to this iframe.
  const expectedOrigin = opaqueOrigin ? 'null' : viewscreenOrigin
  const targetOrigin = opaqueOrigin ? '*' : viewscreenOrigin

  const messageListener = useCallback(
    (event: MessageEvent) =>
      onMessage(event, expectedOrigin, targetOrigin, iFrameRef, containerRef, setErrorMessage, setRenderState),
    [containerRef, expectedOrigin, iFrameRef, targetOrigin],
  )

  useEffect(() => {
    window.addEventListener('message', messageListener)
    return () => window.removeEventListener('message', messageListener)
  }, [messageListener])

  useEffect(() => {
    if (renderState !== RenderState.READY) return
    if (!iFrameRef.current || !containerRef.current) return

    sendDataToIFrame(iFrameRef.current, containerRef.current, previewData, targetOrigin)
  }, [containerRef, iFrameRef, previewData, renderState, targetOrigin])

  return {renderState, errorMessage}
}
